import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  Folder as FolderIcon,
  FolderTree as FolderTreeIcon,
  GripVertical,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import type { Folder } from '@/types/api';
import { cn } from '@/lib/cn';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import {
  useArchiveFolder,
  useDeleteFolder,
  useRenameFolder,
  useUnarchiveFolder,
} from '@/api/folders';
import {
  useAddFolderFavorite,
  useFolderFavorites,
  useRemoveFolderFavorite,
} from '@/api/favorites';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';

interface FolderTreeProps {
  orgId: string;
  folders: Folder[];
  currentFolderId?: string | null;
  onCreateChild?: (parentId: string | null) => void;
  workspaceId?: string | null;
  showArchived?: boolean;
  onToggleArchived?: (next: boolean) => void;
}

interface TreeNode {
  folder: Folder;
  children: TreeNode[];
}

/**
 * Drag payload attached to a folder row when it acts as a draggable.
 * Mirrors the shape used for files (see `FileRow.FileDragData`) so the
 * `onDragEnd` handler can switch on `data.type`.
 */
export interface FolderDragData {
  type: 'folder';
  folderId: string;
  parentId: string | null;
}

function buildTree(folders: Folder[]): TreeNode[] {
  const byParent = new Map<string | null, Folder[]>();
  for (const f of folders) {
    const key = f.parent_id ?? null;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    // Match backend ordering: sort_order first, then name.
    list.sort((a, b) => {
      const aOrd = a.sort_order ?? 0;
      const bOrd = b.sort_order ?? 0;
      if (aOrd !== bOrd) return aOrd - bOrd;
      return a.name.localeCompare(b.name);
    });
  }
  const make = (parent: string | null): TreeNode[] =>
    (byParent.get(parent) ?? []).map((f) => ({ folder: f, children: make(f.id) }));
  return make(null);
}

export function FolderTree({
  orgId,
  folders,
  currentFolderId,
  onCreateChild,
  workspaceId,
  showArchived,
  onToggleArchived,
}: FolderTreeProps) {
  const roots = useMemo(() => buildTree(folders), [folders]);
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();

  // Compute set of folder IDs that are ancestors of the current folder.
  // TreeItem uses this to auto-expand the path to the active folder.
  const ancestorIds = useMemo(() => {
    if (!currentFolderId) return new Set<string>();
    const set = new Set<string>();
    const byId = new Map(folders.map((f) => [f.id, f]));
    let node = byId.get(currentFolderId);
    while (node?.parent_id) {
      set.add(node.parent_id);
      node = byId.get(node.parent_id);
    }
    return set;
  }, [currentFolderId, folders]);

  // Filtered flat list shown when the user types in the search box.
  const filteredFolders = useMemo(() => {
    if (!q) return null;
    return folders.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        (f.path ?? '').toLowerCase().includes(q),
    );
  }, [q, folders]);
  const archive = useArchiveFolder();
  const unarchive = useUnarchiveFolder();
  const rename = useRenameFolder();
  const remove = useDeleteFolder();
  const favorites = useFolderFavorites();
  const addFavorite = useAddFolderFavorite();
  const removeFavorite = useRemoveFolderFavorite();
  const toast = useToast();
  const { hasRole } = useAuth();
  const isStaff = hasRole(['sysadmin', 'manager', 'staff']);
  const canManage = hasRole(['sysadmin', 'manager', 'staff', 'client_admin']);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [favMutatingId, setFavMutatingId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<Folder | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<Folder | null>(null);

  const favoriteIds = useMemo(
    () => new Set((favorites.data ?? []).map((f) => f.folder_id)),
    [favorites.data],
  );

  const canArchive = !!workspaceId;

  const handleToggleFavorite = async (folderId: string) => {
    setFavMutatingId(folderId);
    const isFavorited = favoriteIds.has(folderId);
    try {
      if (isFavorited) {
        await removeFavorite.mutateAsync(folderId);
        toast.success('Unpinned from sidebar');
      } else {
        await addFavorite.mutateAsync(folderId);
        toast.success('Pinned to sidebar');
      }
    } catch (e) {
      toast.error(isFavorited ? 'Unpin failed' : 'Pin failed', apiError(e));
    } finally {
      setFavMutatingId(null);
    }
  };

  const handleArchive = async (folderId: string) => {
    if (!workspaceId) return;
    setMutatingId(folderId);
    try {
      await archive.mutateAsync({ id: folderId, workspace_id: workspaceId });
      toast.success('Folder archived');
    } catch (e) {
      toast.error('Archive failed', apiError(e));
    } finally {
      setMutatingId(null);
    }
  };

  const handleUnarchive = async (folderId: string) => {
    if (!workspaceId) return;
    setMutatingId(folderId);
    try {
      await unarchive.mutateAsync({ id: folderId, workspace_id: workspaceId });
      toast.success('Folder restored');
    } catch (e) {
      toast.error('Unarchive failed', apiError(e));
    } finally {
      setMutatingId(null);
    }
  };

  const openRename = (folder: Folder) => {
    setRenaming(folder);
    setRenameValue(folder.name);
  };

  const submitRename = async () => {
    if (!renaming || !workspaceId) return;
    const name = renameValue.trim();
    if (!name || name === renaming.name) {
      setRenaming(null);
      return;
    }
    setMutatingId(renaming.id);
    try {
      await rename.mutateAsync({ id: renaming.id, name, workspace_id: workspaceId });
      toast.success('Folder renamed');
      setRenaming(null);
    } catch (e) {
      toast.error('Rename failed', apiError(e));
    } finally {
      setMutatingId(null);
    }
  };

  const submitDelete = async () => {
    if (!deleting || !workspaceId) return;
    setMutatingId(deleting.id);
    try {
      await remove.mutateAsync({ id: deleting.id, workspace_id: workspaceId });
      toast.success('Folder deleted');
      setDeleting(null);
    } catch (e) {
      toast.error('Delete failed', apiError(e));
    } finally {
      setMutatingId(null);
    }
  };

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between px-2 mb-1">
        <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">
          Folders
        </span>
        {onCreateChild && (
          <button
            onClick={() => onCreateChild(null)}
            className="p-1 hover:bg-slate-100 text-slate-600"
            aria-label="New root folder"
            title="New folder"
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      {/* Folder filter */}
      <div className="px-2 pb-2">
        <div className="relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter folders…"
            className="w-full pl-6 pr-6 py-1 text-xs border border-[#E3E3F1] rounded-[4px] focus:outline-none focus:ring-1 focus:ring-[#3F4CFF] bg-white placeholder:text-slate-400"
          />
          {filter && (
            <button
              type="button"
              onClick={() => setFilter('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear filter"
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {onToggleArchived && (
        <label className="flex items-center gap-2 px-2 pb-2 text-xs text-slate-600 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={!!showArchived}
            onChange={(e) => onToggleArchived(e.target.checked)}
            className="accent-bb-navy"
          />
          Show archived
        </label>
      )}

      {/* Filtered flat list */}
      {filteredFolders ? (
        filteredFolders.length === 0 ? (
          <p className="px-3 py-4 text-xs text-slate-500 text-center">
            No folders match &ldquo;{filter}&rdquo;
          </p>
        ) : (
          <ul>
            {filteredFolders.map((f) => (
              <li key={f.id}>
                <Link
                  to={`/workspace/${orgId}/folders/${f.id}`}
                  onClick={() => setFilter('')}
                  className={cn(
                    'flex flex-col px-3 py-1.5 hover:bg-slate-50',
                    currentFolderId === f.id && 'bg-[#EFF0FF]',
                  )}
                >
                  <span
                    className={cn(
                      'text-sm truncate',
                      currentFolderId === f.id
                        ? 'text-[#0410BD] font-medium'
                        : 'text-slate-700',
                    )}
                  >
                    {f.name}
                  </span>
                  {f.path && f.path !== f.name && (
                    <span className="text-[10px] text-slate-400 truncate">{f.path}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : roots.length === 0 ? (
        <EmptyState
          title="No folders"
          description="No folders in this workspace."
          icon={<FolderTreeIcon size={24} />}
          action={
            isStaff ? (
              <Link
                to="/admin/templates"
                className="inline-flex items-center gap-2 px-3 py-2 border border-bb-lilac bg-white text-bb-navy hover:bg-bb-navy/5 font-medium"
              >
                Apply a template
              </Link>
            ) : undefined
          }
        />
      ) : (
        <ul>
          {roots.map((n) => (
            <TreeItem
              key={n.folder.id}
              node={n}
              orgId={orgId}
              depth={0}
              currentFolderId={currentFolderId ?? null}
              ancestorIds={ancestorIds}
              onCreateChild={onCreateChild}
              canArchive={canArchive}
              canManage={canManage}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onRename={openRename}
              onDelete={(f) => setDeleting(f)}
              mutatingId={mutatingId}
              favoriteIds={favoriteIds}
              onToggleFavorite={handleToggleFavorite}
              favMutatingId={favMutatingId}
            />
          ))}
        </ul>
      )}

      <Modal
        open={!!renaming}
        onClose={() => setRenaming(null)}
        title="Rename folder"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitRename}
              loading={rename.isPending}
              disabled={!renameValue.trim()}
              className="w-auto"
            >
              Rename
            </Button>
          </>
        }
      >
        <Label htmlFor="folder-rename-input">New name</Label>
        <Input
          id="folder-rename-input"
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitRename();
          }}
        />
      </Modal>

      <Modal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete folder"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitDelete}
              loading={remove.isPending}
              className="w-auto"
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Delete the folder <span className="font-semibold">{deleting?.name}</span>?
          This only works if the folder contains no files or sub-folders. Use
          Archive instead to hide a folder while keeping its contents.
        </p>
      </Modal>
    </div>
  );
}

function TreeItem({
  node,
  orgId,
  depth,
  currentFolderId,
  ancestorIds,
  onCreateChild,
  canArchive,
  canManage,
  onArchive,
  onUnarchive,
  onRename,
  onDelete,
  mutatingId,
  favoriteIds,
  onToggleFavorite,
  favMutatingId,
}: {
  node: TreeNode;
  orgId: string;
  depth: number;
  currentFolderId: string | null;
  ancestorIds: Set<string>;
  onCreateChild?: (parentId: string | null) => void;
  canArchive: boolean;
  canManage: boolean;
  onArchive: (folderId: string) => void;
  onUnarchive: (folderId: string) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  mutatingId: string | null;
  favoriteIds: Set<string>;
  onToggleFavorite: (folderId: string) => void;
  favMutatingId: string | null;
}) {
  const [open, setOpen] = useState(
    depth < 1 || ancestorIds.has(node.folder.id),
  );

  // Auto-expand when this folder becomes an ancestor of the active folder.
  useEffect(() => {
    if (ancestorIds.has(node.folder.id)) setOpen(true);
  }, [ancestorIds, node.folder.id]);
  const hasChildren = node.children.length > 0;
  const isActive = currentFolderId === node.folder.id;
  const isArchived = !!node.folder.is_archived;
  const isMutating = mutatingId === node.folder.id;
  const isFavorited = favoriteIds.has(node.folder.id);
  const isFavMutating = favMutatingId === node.folder.id;

  // Draggable + sortable for reorder/nest. The id encodes the type so the
  // top-level DndContext can disambiguate file vs folder drops.
  const dragData: FolderDragData = {
    type: 'folder',
    folderId: node.folder.id,
    parentId: node.folder.parent_id,
  };
  const sortable = useSortable({
    id: `folder:${node.folder.id}`,
    data: dragData,
    disabled: isArchived,
  });

  // Separate droppable that ONLY accepts file drops — gives us a distinct
  // hit-test target so file drops always land on the row even if the
  // sortable's collision detection prefers another folder.
  const fileDrop = useDroppable({
    id: `folder-file-target:${node.folder.id}`,
    data: { type: 'folder-file-target', folderId: node.folder.id },
    disabled: isArchived,
  });

  // Cap visual indent so deeply nested rows still have room for the name.
  // The full ancestry is still available via the folder.path tooltip.
  const indentDepth = Math.min(depth, 8);
  const transformStyle: CSSProperties = {
    paddingLeft: 6 + indentDepth * 12,
    transform: sortable.transform
      ? `translate3d(${sortable.transform.x}px, ${sortable.transform.y}px, 0)`
      : undefined,
    transition: sortable.transition ?? undefined,
  };

  // Active drop target highlight: dashed ring whenever this row is being
  // hovered by either kind of drag (file or folder).
  const isOver = sortable.isOver || fileDrop.isOver;
  const isDragging = sortable.isDragging;

  // Compose the two ref callbacks so the row registers as both a sortable
  // and a file drop target.
  const setRefs = (el: HTMLElement | null): void => {
    sortable.setNodeRef(el);
    fileDrop.setNodeRef(el);
  };

  return (
    <li>
      <div
        ref={setRefs}
        className={cn(
          'relative flex items-center gap-0.5 pl-2 pr-6 py-1 group hover:bg-slate-50',
          isActive && 'bg-bb-navy/10',
          isArchived && 'opacity-60',
          isDragging && 'opacity-50',
          isOver && !isDragging && 'border border-dashed border-bb-navy',
        )}
        style={transformStyle}
      >
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-[2px] p-0.5 text-slate-500 hover:text-bb-navy shrink-0"
          aria-label={open ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            open ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )
          ) : (
            <span className="inline-block w-3.5" />
          )}
        </button>
        {/* Drag handle: only this element initiates a drag, so the folder
            link below remains clickable for navigation. */}
        <button
          type="button"
          className={cn(
            'shrink-0 mt-[2px] p-0.5 text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(pointer:coarse)]:opacity-100',
            isArchived && 'invisible',
          )}
          aria-label={`Drag ${node.folder.name}`}
          title="Drag to reorder or nest"
          {...sortable.listeners}
          {...sortable.attributes}
        >
          <GripVertical size={12} />
        </button>
        <Link
          to={`/workspace/${orgId}/folders/${node.folder.id}`}
          title={node.folder.path || node.folder.name}
          className={cn(
            'flex-1 min-w-0 inline-flex items-start gap-1.5 py-0.5',
            isActive ? 'text-bb-navy font-medium' : 'text-slate-700',
          )}
        >
          <FolderIcon size={14} className="text-bb-lilac shrink-0 mt-[3px]" />
          <span className="truncate">
            {node.folder.name}
          </span>
          {isArchived && (
            <Badge tone="gray-strong" className="ml-1 shrink-0">
              Archived
            </Badge>
          )}
        </Link>
        {canArchive && (
          isArchived ? (
            <button
              type="button"
              onClick={() => onUnarchive(node.folder.id)}
              disabled={isMutating}
              className="shrink-0 mt-[2px] opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-0.5 text-slate-500 hover:text-bb-navy disabled:opacity-40"
              aria-label="Unarchive folder"
              title="Unarchive folder"
            >
              <ArchiveRestore size={12} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onArchive(node.folder.id)}
              disabled={isMutating}
              className="shrink-0 mt-[2px] opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-0.5 text-slate-500 hover:text-bb-navy disabled:opacity-40"
              aria-label="Archive folder"
              title="Archive folder"
            >
              <Archive size={12} />
            </button>
          )
        )}
        {onCreateChild && !isArchived && (
          <button
            onClick={() => onCreateChild(node.folder.id)}
            className="shrink-0 mt-[2px] opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-0.5 text-slate-500 hover:text-bb-navy"
            aria-label="New subfolder"
            title="New subfolder"
          >
            <Plus size={12} />
          </button>
        )}
        {canManage && !isArchived && (
          <button
            type="button"
            onClick={() => onRename(node.folder)}
            disabled={isMutating}
            className="shrink-0 mt-[2px] opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-0.5 text-slate-500 hover:text-bb-navy disabled:opacity-40"
            aria-label="Rename folder"
            title="Rename folder"
          >
            <Pencil size={12} />
          </button>
        )}
        {canManage && !isArchived && (
          <button
            type="button"
            onClick={() => onDelete(node.folder)}
            disabled={isMutating}
            className="shrink-0 mt-[2px] opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-0.5 text-slate-500 hover:text-red-600 disabled:opacity-40"
            aria-label="Delete folder"
            title="Delete folder (folder must be empty)"
          >
            <Trash2 size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onToggleFavorite(node.folder.id)}
          disabled={isFavMutating}
          className={cn(
            'absolute right-1 top-2 p-0.5 transition-opacity disabled:opacity-40',
            isFavorited
              ? 'opacity-100 text-yellow-500 hover:text-yellow-600'
              : 'opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(pointer:coarse)]:opacity-100 text-slate-400 hover:text-yellow-500',
          )}
          aria-label={isFavorited ? 'Unpin from sidebar' : 'Pin to sidebar'}
          title={isFavorited ? 'Unpin from sidebar' : 'Pin to sidebar'}
          aria-pressed={isFavorited}
        >
          <Star
            size={12}
            className={cn(isFavorited && 'fill-yellow-400 text-yellow-400')}
          />
        </button>
      </div>
      {open && hasChildren && (
        <ul>
          {node.children.map((child) => (
            <TreeItem
              key={child.folder.id}
              node={child}
              orgId={orgId}
              depth={depth + 1}
              currentFolderId={currentFolderId}
              ancestorIds={ancestorIds}
              onCreateChild={onCreateChild}
              canArchive={canArchive}
              canManage={canManage}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onRename={onRename}
              onDelete={onDelete}
              mutatingId={mutatingId}
              favoriteIds={favoriteIds}
              onToggleFavorite={onToggleFavorite}
              favMutatingId={favMutatingId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
